/**
 * Часы работы и выходные дни.
 *
 * Расписание отправляется ЦЕЛИКОМ, семью днями за раз: форма на телефоне не
 * должна слать семь запросов и застревать в полусохранённом состоянии, когда
 * третий из них не дошёл.
 *
 * Дни показываются с понедельника, хотя в базе неделя начинается с
 * воскресенья (`weekday` 0 — как `EXTRACT(DOW)`). Переставляется только
 * порядок вывода: сами значения те же, что на сервере.
 */
import { useState } from 'react';
import type { CurrentUser, VendorDayOff, VendorScheduleDay } from '@arbuz/shared';
import { ApiError, api } from '../api';
import { makeT } from '../i18n';
import { haptic } from '../tg';
import { inputStyle, secondaryButton } from './courierUi';
import { EmptyState, bottomBarStyle, errorTextStyle, hintStyle, pageWithBottomBar } from './vendorUi';

interface Props {
  user: CurrentUser;
  days: VendorScheduleDay[] | null;
  daysOff: VendorDayOff[] | null;
  error: string | null;
  onSaved: () => void;
  onRetry: () => void;
  onBack: () => void;
}

export function VendorSchedule({ user, days, daysOff, error, onSaved, onRetry, onBack }: Props) {
  const t = makeT(user.locale);

  const [draft, setDraft] = useState<VendorScheduleDay[] | null>(days);
  const [newDate, setNewDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Первая отрисовка приходит раньше данных — подхватываем их, когда пришли
  if (draft === null && days !== null) setDraft(days);

  if (error) {
    return (
      <main className="page">
        <h1 className="h1">{t('scheduleTitle')}</h1>
        <EmptyState tone="error" title={error} actionLabel={t('retry')} onAction={onRetry} />
      </main>
    );
  }

  if (draft === null) {
    return (
      <main className="page">
        <h1 className="h1">{t('scheduleTitle')}</h1>
        <p className="muted">{t('loading')}</p>
      </main>
    );
  }

  const patchDay = (weekday: number, patch: Partial<VendorScheduleDay>) => {
    setSaved(false);
    setDraft((prev) => prev?.map((d) => (d.weekday === weekday ? { ...d, ...patch } : d)) ?? null);
  };

  async function save() {
    if (!draft) return;

    // Те же два правила, что проверит сервер. Дублируются сознательно: отказ
    // после отправки на седьмом поле объясняет меньше, чем подсказка на месте.
    if (draft.every((d) => d.is_closed)) {
      setFormError(t('scheduleAllClosed'));
      haptic('error');
      return;
    }
    if (draft.some((d) => !d.is_closed && d.work_start >= d.work_end)) {
      setFormError(t('scheduleBadHours'));
      haptic('error');
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      await api.put<{ days: VendorScheduleDay[] }>('/api/vendor/schedule', { days: draft });
      haptic('success');
      setSaved(true);
      onSaved();
    } catch (err) {
      haptic('error');
      setFormError(err instanceof ApiError ? err.message : t('actionFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function addDayOff() {
    if (!newDate) return;
    setSaving(true);
    try {
      await api.post<VendorDayOff>('/api/vendor/days-off', { slot_date: newDate });
      setNewDate('');
      haptic('light');
      onSaved();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t('actionFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function removeDayOff(id: number) {
    setSaving(true);
    try {
      await api.delete(`/api/vendor/days-off/${id}`);
      haptic('light');
      onSaved();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t('actionFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="page" style={pageWithBottomBar}>
      <h1 className="h1">{t('scheduleTitle')}</h1>
      <p style={hintStyle}>{t('scheduleHint')}</p>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--s-3)' }}>
        {draft.map((d) => (
          <li
            key={d.weekday}
            style={{
              padding: 'var(--s-3)',
              borderRadius: 'var(--r-lg)',
              border: '1px solid var(--c-border)',
              background: 'var(--c-surface)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--s-3)',
              }}
            >
              <span style={{ font: 'var(--fw-medium) var(--fs-body)/var(--lh-body) var(--font)' }}>
                {t(`weekday_${d.weekday}` as 'weekday_0')}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={d.is_closed}
                onClick={() => patchDay(d.weekday, { is_closed: !d.is_closed })}
                style={{
                  minHeight: 'var(--tap-min)',
                  padding: '0 var(--s-3)',
                  borderRadius: 'var(--r-full)',
                  border: `1px solid ${d.is_closed ? 'var(--c-border-input)' : 'var(--c-primary-text)'}`,
                  background: d.is_closed ? 'var(--c-surface)' : 'var(--c-primary-subtle)',
                  color: d.is_closed ? 'var(--c-text-secondary)' : 'var(--c-primary-text)',
                  font: 'var(--fw-medium) var(--fs-sm)/var(--lh-sm) var(--font)',
                  cursor: 'pointer',
                }}
              >
                {d.is_closed ? t('scheduleDayOff') : t('shopOpen')}
              </button>
            </div>

            {!d.is_closed && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto 1fr',
                  alignItems: 'center',
                  gap: 'var(--s-2)',
                  marginTop: 'var(--s-3)',
                }}
              >
                <label htmlFor={`from-${d.weekday}`} style={{ fontSize: 'var(--fs-sm)' }}>
                  {t('scheduleFrom')}
                </label>
                <input
                  id={`from-${d.weekday}`}
                  type="time"
                  value={d.work_start}
                  onChange={(e) => patchDay(d.weekday, { work_start: e.target.value })}
                  style={inputStyle}
                />
                <label htmlFor={`to-${d.weekday}`} style={{ fontSize: 'var(--fs-sm)' }}>
                  {t('scheduleTo')}
                </label>
                <input
                  id={`to-${d.weekday}`}
                  type="time"
                  value={d.work_end}
                  onChange={(e) => patchDay(d.weekday, { work_end: e.target.value })}
                  style={inputStyle}
                />
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* ── Отдельные выходные даты ──────────────────────────────────────── */}
      <section style={{ marginTop: 'var(--s-6)' }}>
        <h2 style={{ fontSize: 'var(--fs-h3)', lineHeight: 'var(--lh-h3)', margin: '0 0 var(--s-2)' }}>
          {t('daysOffTitle')}
        </h2>
        <p style={hintStyle}>{t('daysOffHint')}</p>

        {daysOff && daysOff.length === 0 && (
          <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
            {t('daysOffEmpty')}
          </p>
        )}

        {daysOff && daysOff.length > 0 && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--s-2)' }}>
            {daysOff.map((d) => (
              <li
                key={d.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--s-3)',
                  minHeight: 'var(--tap-min)',
                }}
              >
                <span>{d.slot_date}</span>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void removeDayOff(d.id)}
                  style={{
                    minHeight: 'var(--tap-min)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--c-danger)',
                    font: 'inherit',
                    cursor: 'pointer',
                    padding: '0 var(--s-2)',
                  }}
                >
                  {t('daysOffRemove')}
                </button>
              </li>
            ))}
          </ul>
        )}

        <label
          htmlFor="day-off-date"
          style={{
            display: 'block',
            marginTop: 'var(--s-3)',
            marginBottom: 'var(--s-2)',
            fontSize: 'var(--fs-sm)',
            color: 'var(--c-text-secondary)',
          }}
        >
          {t('daysOffAdd')}
        </label>
        <div style={{ display: 'flex', gap: 'var(--s-2)' }}>
          <input
            id="day-off-date"
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            type="button"
            style={{ ...secondaryButton, width: 'auto', padding: '0 var(--s-4)' }}
            disabled={!newDate || saving}
            onClick={() => void addDayOff()}
          >
            {t('daysOffAdd')}
          </button>
        </div>
      </section>

      {formError && (
        <p role="alert" style={errorTextStyle}>
          {formError}
        </p>
      )}
      {saved && !formError && (
        <p role="status" style={{ ...errorTextStyle, color: 'var(--c-primary-text)' }}>
          {t('scheduleSaved')}
        </p>
      )}

      <div style={bottomBarStyle}>
        <button
          type="button"
          className="btn-primary"
          disabled={saving}
          data-loading={saving ? 'true' : undefined}
          onClick={() => void save()}
        >
          {t('scheduleSave')}
        </button>
        <button type="button" style={secondaryButton} onClick={onBack}>
          {t('shopTitle')}
        </button>
      </div>
    </main>
  );
}
